import * as React from 'react'
import { ReactNode } from 'react'
import {
  LuChevronLeft,
  LuChevronRight,
  LuChevronsLeft,
  LuChevronsRight,
} from 'react-icons/lu'

import { type Button, buttonVariants } from '@/components/ui/button'
import { PaginatorInfo } from '@/lib/paginatorInfo'
import { cn } from '@/lib/utils'

export interface PaginationProps {
  totalItemsCount: number
  onChange: (pageNumber: number) => void
  activePage?: number
  numItemsPerPage?: number
  pageRangeDisplayed?: number
  className?: string
}

function Pagination({
  totalItemsCount,
  onChange,
  activePage = 1,
  numItemsPerPage = 10,
  pageRangeDisplayed = 5,
  className,
}: PaginationProps) {
  function buildPages() {
    const paginationInfo = new PaginatorInfo(
      numItemsPerPage,
      pageRangeDisplayed,
    ).build(totalItemsCount, activePage)

    const pageNodes: ReactNode[] = []

    if (paginationInfo.hasPreviousPage) {
      pageNodes.push(
        <PaginationItem key="first">
          <PaginationLink onClick={() => onChange(1)}>
            <LuChevronsLeft />
          </PaginationLink>
        </PaginationItem>,

        <PaginationItem key="previous">
          <PaginationPrevious
            onClick={() => onChange(paginationInfo.previousPage)}
          />
        </PaginationItem>,
      )
    }

    for (
      let pageNum = paginationInfo.firstPage;
      pageNum <= paginationInfo.lastPage;
      pageNum++
    ) {
      pageNodes.push(
        <PaginationItem key={pageNum}>
          <PaginationLink
            isActive={pageNum === activePage}
            onClick={() => onChange(pageNum)}
          >
            {pageNum}
          </PaginationLink>
        </PaginationItem>,
      )
    }

    if (paginationInfo.hasNextPage) {
      pageNodes.push(
        <PaginationItem key="next">
          <PaginationNext onClick={() => onChange(paginationInfo.nextPage)} />
        </PaginationItem>,

        <PaginationItem key="last">
          <PaginationLink onClick={() => onChange(paginationInfo.totalPages)}>
            <LuChevronsRight />
          </PaginationLink>
        </PaginationItem>,
      )
    }

    return pageNodes
  }

  const pages = buildPages()

  return (
    <PaginationContainer className={className}>
      <PaginationContent>{pages}</PaginationContent>
    </PaginationContainer>
  )
}

export function PaginationContainer({
  className,
  ...props
}: React.ComponentProps<'nav'>) {
  return (
    <nav
      role="navigation"
      aria-label="pagination"
      data-slot="pagination"
      className={cn('mx-auto flex w-full justify-center', className)}
      {...props}
    />
  )
}

function PaginationContent({
  className,
  ...props
}: React.ComponentProps<'ul'>) {
  return (
    <ul
      data-slot="pagination-content"
      className={cn(
        'mb-4',
        'flex list-none flex-row items-center gap-1',
        className,
      )}
      {...props}
    />
  )
}

function PaginationItem({ ...props }: React.ComponentProps<'li'>) {
  return <li data-slot="pagination-item" {...props} />
}

type PaginationLinkProps = {
  isActive?: boolean
} & Pick<React.ComponentProps<typeof Button>, 'size'> &
  React.ComponentProps<'a'>

function PaginationLink({
  className,
  isActive,
  size = 'icon',
  ...props
}: PaginationLinkProps) {
  return (
    <a
      aria-current={isActive ? 'page' : undefined}
      data-slot="pagination-link"
      data-active={isActive}
      className={cn(
        'cursor-pointer text-black',
        buttonVariants({
          variant: isActive ? 'black' : 'ghost',
          size,
        }),
        className,
      )}
      {...props}
    />
  )
}

function PaginationPrevious({
  className,
  ...props
}: React.ComponentProps<typeof PaginationLink>) {
  return (
    <PaginationLink
      aria-label="Go to previous page"
      size="default"
      className={cn('gap-1 px-2.5 sm:pl-2.5', className)}
      {...props}
    >
      <LuChevronLeft />
    </PaginationLink>
  )
}

function PaginationNext({
  className,
  ...props
}: React.ComponentProps<typeof PaginationLink>) {
  return (
    <PaginationLink
      aria-label="Go to next page"
      size="default"
      className={cn('gap-1 px-2.5 sm:pr-2.5', className)}
      {...props}
    >
      <LuChevronRight />
    </PaginationLink>
  )
}

export {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
}
